import {StorageHelpers, toAbsoluteUrl} from '../../../../_gori/helpers'
import {useEffect, useMemo, useRef, useState} from 'react'
import {CHATBOT_WELCOME_VISIBLE_KEY} from '../../../../_gori/constants'
import {ChatbotWidget} from '../../chatbot'
import {ChatbotContextProvider} from '../../chatbot/context'
import {v4 as uuid} from 'uuid'
import {
  CreateMessagePayload,
  CreateMessageResponse,
  HandleCreateMessageParams,
  MESSAGE_CONTAINER_ID,
  MESSAGE_TYPE,
  MessageContent,
  MessageModel,
  MessageType,
  SENDER_TYPE,
  TYPING_TEXT_DURATION,
} from '../../chatbot/definitions'
import {CHATBOT_MODE, ChatbotMode} from '../../chatbot/definitions/type'
import {usePopper} from 'react-popper'
import ChatbotService from '../../chatbot/services/request'
import {isEmpty} from 'lodash'
import clsx from 'clsx'
import {useIntl} from 'react-intl'

const LARGE_WIDTH = '785px'
const DEFAULT_WIDTH = '384px'

const TIMER_UPDATE = 100

const HelpCenterWidget = function (props) {
  const [showPopover, setShowPopover] = useState<boolean>(false)
  const [messages, setMessages] = useState<MessageModel<MessageContent>[]>([])
  const [mode, setMode] = useState<ChatbotMode>(CHATBOT_MODE.DEFAULT)
  const [referenceElement, setReferenceElement] = useState<any>(null)
  const [popperElement, setPopperElement] = useState<any>(null)
  const [arrowElement, setArrowElement] = useState<any>(null)
  const [isSendingMessage, setIsSendingMessage] = useState(false)
  const [isMessageResponded, setIsMessageResponded] = useState(true)
  const [showWelcome, setShowWelcome] = useState(true)

  const updateTimerId = useRef<ReturnType<typeof setTimeout> | null>(null)
  const scrollTimerId = useRef<ReturnType<typeof setTimeout> | null>(null)

  const intl = useIntl()

  const isLargeMode = useMemo(() => mode === CHATBOT_MODE.LARGE, [mode])

  const disabledUserAction = useMemo(
    () => isSendingMessage || !isMessageResponded,
    [isMessageResponded, isSendingMessage]
  )

  // NOTE use Popper lib instead of Bootstrap's Popover to resize
  const {styles, attributes, update} = usePopper(referenceElement, popperElement, {
    modifiers: [
      {name: 'arrow', options: {element: arrowElement}},
      {
        name: 'offset',
        options: {
          offset: [0, 6],
        },
      },
    ],
    placement: 'top-end',
  })

  const clearUpdateTimer = () => {
    if (updateTimerId.current) {
      clearTimeout(updateTimerId.current)
    }
  }

  useEffect(() => {
    const isWelcomeClosed = StorageHelpers.getItemLocalStorage(CHATBOT_WELCOME_VISIBLE_KEY)
    if (isWelcomeClosed === 'true') {
      setShowWelcome(false)
    }
  }, [])

  useEffect(() => {
    const isWelcomeVisible = StorageHelpers.getItemLocalStorage(CHATBOT_WELCOME_VISIBLE_KEY)
    setShowWelcome(!!isWelcomeVisible)
  }, [])

  const handleCloseWelcome = () => {
    setShowWelcome(false)
    StorageHelpers.setItemLocalStorage(CHATBOT_WELCOME_VISIBLE_KEY, false)
  }

  useEffect(() => {
    if (!update) return

    clearUpdateTimer()

    updateTimerId.current = setTimeout(() => {
      update()
    }, TIMER_UPDATE)

    return () => clearUpdateTimer()
  }, [isLargeMode, update])

  const addMessage = (message: any) => {
    const newMessage = {...message, id: message?.id || uuid()}
    setMessages((prevMessages) => [newMessage, ...prevMessages])
  }

  const addMessages = (message: any[]) => {
    const newMessages = message.map((msg) => ({...msg, id: msg?.id || uuid()}))
    setMessages((prevMessages) => [...prevMessages, ...newMessages])
  }

  const patchMessage = (id, data) => {
    setMessages((prevMessages) => {
      const targetMessage = prevMessages.find((msg) => msg.id === id)
      if (!targetMessage) return prevMessages

      return prevMessages.map((msg) => (msg.id === id ? {...msg, ...data} : msg))
    })
  }

  const putMessage = (id, data) => {
    setMessages((prevMessages) => {
      const targetMessage = prevMessages.find((msg) => msg.id === id)
      if (!targetMessage) return prevMessages

      return prevMessages.map((msg) => (msg.id === id ? {...data} : msg))
    })
  }

  const findMessageById = (id: number | string) => {
    return messages.find((msg) => msg.id === id)
  }

  const handleCreateMessage = async ({
    message,
    type,
    newMessageId = '',
    currentMessageId,
  }: HandleCreateMessageParams) => {
    if (scrollTimerId.current) clearTimeout(scrollTimerId.current)

    scrollTimerId.current = setTimeout(() => {
      const container = document.getElementById(MESSAGE_CONTAINER_ID)
      if (container) {
        container.scrollTo({
          top: 0,
          behavior: 'smooth',
        })
      }
    }, 100)

    try {
      setIsSendingMessage(true)
      setIsMessageResponded(false)
      const messagePayload = message

      if (
        (
          [
            MESSAGE_TYPE.ADDRESS,
            MESSAGE_TYPE.DATE_PICKER,
            MESSAGE_TYPE.DATE_RANGE_PICKER,
            MESSAGE_TYPE.SELECT_SERVICE,
            MESSAGE_TYPE.ORDER_INFO,
            MESSAGE_TYPE.CONFIRM_ORDER_INFO,
          ] as MessageType[]
        ).includes(type)
      ) {
        messagePayload.disabled = true
      }

      const payload: CreateMessagePayload = {
        message: messagePayload,
        type,
      }
      if (currentMessageId) {
        payload.id = currentMessageId
      }

      const response = await ChatbotService.createMessage(payload)
      setIsSendingMessage(false)
      if (isEmpty(response)) {
        setIsMessageResponded(true)
        return
      }
      await renderResponseMessages({response, currentMessageId})
    } catch (error) {
      setIsSendingMessage(false)
      patchMessage(newMessageId, {
        error: 'Failed to send message.',
      })
    } finally {
      setIsMessageResponded(true)
    }
  }

  const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

  const renderResponseMessages = async ({
    response,
    currentMessageId,
  }: {
    response: CreateMessageResponse
    currentMessageId?: string | number
  }) => {
    let _currentMessageId = currentMessageId

    for (const messageItem of response) {
      const responseMessageId = messageItem.id

      switch (messageItem.type) {
        case MESSAGE_TYPE.TEXT: {
          const parts = messageItem.content || []
          let content = ''
          setIsMessageResponded(false)

          const typingDuration =
            parts.length >= 50 ? TYPING_TEXT_DURATION.FASTER : TYPING_TEXT_DURATION.NORMAL

          for (let i = 0; i < parts.length; i++) {
            content += parts[i]

            if (i === 0) {
              addMessage({
                content,
                sender: SENDER_TYPE.ASSISTANT,
                type: messageItem.type,
                id: responseMessageId,
                created_at: messageItem.created_at,
              })
            } else {
              patchMessage(responseMessageId, {content})
            }

            await sleep(typingDuration)
          }

          break
        }

        case MESSAGE_TYPE.DROPDOWN_SELECT:
        case MESSAGE_TYPE.DATE_RANGE_PICKER: {
          if (!_currentMessageId) {
            addMessage({
              id: responseMessageId,
              content: messageItem.content,
              sender: SENDER_TYPE.ASSISTANT,
              type: messageItem.type,
              created_at: messageItem.created_at,
            })
          }
          break
        }

        default: {
          addMessage({
            id: responseMessageId,
            content: messageItem.content,
            sender: SENDER_TYPE.ASSISTANT,
            type: messageItem.type,
            created_at: messageItem.created_at,
          })
          break
        }
      }
      // NOTE: rest after first message
      if (_currentMessageId) {
        _currentMessageId = ''
      }
    }
  }

  useEffect(() => {
    return () => {
      if (scrollTimerId.current) {
        clearTimeout(scrollTimerId.current)
      }
    }
  }, [])

  useEffect(() => {
    if (showPopover) {
      handleCloseWelcome()
    }
  }, [showPopover])

  return (
    <>
      <div>
        {showWelcome && !showPopover && (
          <div className='position-relative bg-primary' style={{zIndex: '1000'}}>
            <div className='help-center-welcome bg-image-gori'>
              <span className='text-white'>
                {intl.formatMessage({id: 'CHATBOT_WELCOME_MESSAGE_INTRODUCE'})}
              </span>
              <button className='welcome-close' onClick={handleCloseWelcome} aria-label='close'>
                ×
              </button>
            </div>
          </div>
        )}
        <ChatbotContextProvider
          value={{
            messages,
            setMessages,
            addMessage,
            addMessages,
            patchMessage,
            putMessage,
            mode,
            setMode,
            findMessageById,
            handleCreateMessage,
            isSendingMessage,
            isMessageResponded,
            disabledUserAction,
          }}
        >
          <button
            ref={setReferenceElement}
            onClick={() => {
              setShowPopover((prev) => !prev)
            }}
            id='btn-help-center-widget'
            className={clsx(
              'btn-help-center-widget p-0 w-48px h-48px rounded-circle d-flex justify-content-center align-items-center bg-hover-warning border-3 border border-warning',
              {
                'bg-warning': showPopover,
                'bg-white': !showPopover,
              }
            )}
            children={<img alt='chatbot' src={toAbsoluteUrl(`/media/gori/chatbot/avatar.svg`)} />}
          />

          {
            // zIndex: 1054, // Ensure it appears lower than the modal (z-index of modal: 1055)
            showPopover && (
              <div
                ref={setPopperElement}
                style={{...styles.popper, zIndex: 1054}}
                {...attributes.popper}
              >
                <div
                  style={{
                    width: isLargeMode ? LARGE_WIDTH : DEFAULT_WIDTH,
                    height: isLargeMode ? '84vh' : '600px',
                    transition: 'all 0.3s ease-in-out',
                  }}
                  className='bg-white shadow-lg rounded-2 overflow-hidden'
                >
                  <ChatbotWidget
                    onHide={() => {
                      setShowPopover(false)
                      setMode(CHATBOT_MODE.DEFAULT)
                    }}
                  />
                </div>
                <div ref={setArrowElement} style={styles.arrow} />
              </div>
            )
          }
        </ChatbotContextProvider>
      </div>
    </>
  )
}

export {HelpCenterWidget}
